/**
 * СИЗ по нормам (СК-27): нормы компании на профессию, список «кому выдать или заменить», карточка
 * сотрудника и выдача документом выдачи (получатель подтверждает в своём приложении).
 */
import {
  canCompanyWide,
  PPE_STATES,
  type PpeCard,
  type PpeDueRow,
  type PpeIssueInput,
  type PpeNorm,
  type PpeNormInput,
  type PpeState,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { itemsQuery, parseNum, peopleQuery, qty, warehousesQuery } from './queries';

const STATE_TONE: Record<PpeState, 'neutral' | 'success' | 'warning' | 'danger'> = {
  missing: 'neutral',
  ok: 'success',
  soon: 'warning',
  overdue: 'danger',
};

const normsQuery = {
  queryKey: ['wh', 'ppe', 'norms'],
  queryFn: () => api<PpeNorm[]>('/warehouse/ppe/norms'),
};
const dueQuery = { queryKey: ['wh', 'ppe', 'due'], queryFn: () => api<PpeDueRow[]>('/warehouse/ppe/due') };
const cardQuery = (userId: string) => ({
  queryKey: ['wh', 'ppe', 'card', userId],
  queryFn: () => api<PpeCard>(`/warehouse/ppe/card/${userId}`),
});

export function PpeTab() {
  const me = useMe();
  const warehouses = useQuery(warehousesQuery);
  const canOperate = (warehouses.data ?? []).some((w) => w.canOperate);
  // Нормы и список по всей компании — право «Проводить по складу» на уровне компании, как на сервере
  const companyWide = canCompanyWide(me.access, 'warehouse.operate');
  const [userId, setUserId] = useState(me.user.id);
  const [issueFor, setIssueFor] = useState<PpeCard | null>(null);
  const people = useQuery(peopleQuery);
  const card = useQuery(cardQuery(userId));
  return (
    <div className="flex flex-col gap-6">
      {companyWide ? <DuePanel onIssue={(id) => setUserId(id)} /> : null}
      <Panel
        title="Карточка выдачи СИЗ"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canOperate ? (
              <Select
                className="h-11 w-auto max-w-[260px]"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              >
                {(people.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fullName}
                    {p.position ? ` · ${p.position}` : ''}
                  </option>
                ))}
              </Select>
            ) : null}
            {canOperate && card.data ? (
              <Button variant="primary" onClick={() => setIssueFor(card.data)}>
                Выдать СИЗ
              </Button>
            ) : null}
          </div>
        }
      >
        {card.isPending ? (
          <Spinner />
        ) : card.isError ? (
          <ErrorBox error={card.error} />
        ) : (
          <CardView card={card.data} canOperate={canOperate} />
        )}
      </Panel>
      {companyWide ? <NormsPanel /> : null}
      {issueFor ? <IssueDialog card={issueFor} onClose={() => setIssueFor(null)} /> : null}
    </div>
  );
}

function CardView({ card, canOperate }: { card: PpeCard; canOperate: boolean }) {
  const qc = useQueryClient();
  const close = useMutation({
    mutationFn: (p: { id: string; note: string }) =>
      api<PpeCard>(`/warehouse/ppe/issues/${p.id}/close`, { body: { note: p.note } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wh', 'ppe'] });
      toast('Запись закрыта');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <div className="flex flex-col gap-4">
      <p className="text-[14px] text-ink-2">
        {card.fullName}
        {card.profession ? ` · ${card.profession}` : ' · должность не указана — нормы не подобраны'}
      </p>
      {card.rows.length ? (
        <ul className="divide-y divide-line">
          {card.rows.map((r) => (
            <li
              key={`${r.itemId}-${r.normId ?? 'x'}`}
              className="flex flex-col gap-1 py-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4"
            >
              <div className="min-w-0 flex-1">
                <Link
                  to="/warehouse/items/$itemId"
                  params={{ itemId: r.itemId }}
                  className="font-medium hover:underline"
                >
                  {r.itemName}
                </Link>
                <span className="num ml-2 text-[13px] text-ink-2">
                  {qty(r.quantity)} {r.unit} · срок носки {r.wearMonths} мес.{r.normId ? '' : ' · вне нормы'}
                </span>
                {r.issue ? (
                  <div className="text-[13px] text-ink-2">
                    выдано {date(r.issue.issuedOn)}
                    {r.issue.documentNumber ? (
                      <>
                        {' '}
                        по{' '}
                        <Link
                          to="/warehouse/documents/$docId"
                          params={{ docId: r.issue.documentId ?? '' }}
                          className="underline"
                        >
                          {r.issue.documentNumber}
                        </Link>
                      </>
                    ) : null}
                    {r.issue.confirmed ? '' : ' · получение не подтверждено'} · заменить до{' '}
                    {date(r.issue.replaceOn)}
                  </div>
                ) : null}
              </div>
              <Badge tone={STATE_TONE[r.state]}>{PPE_STATES[r.state]}</Badge>
              {canOperate && r.issue ? (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={close.isPending && close.variables?.id === r.issue.id}
                  onClick={() => {
                    const note = window.prompt('Почему закрывается запись (утеря, увольнение, списание)?');
                    if (note?.trim() && r.issue) close.mutate({ id: r.issue.id, note: note.trim() });
                  }}
                >
                  Закрыть
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="Выдач нет">
          Нормы на эту профессию не заведены или СИЗ ещё не выдавались.
        </EmptyState>
      )}
      {card.history.some((h) => h.closedOn) ? (
        <details className="text-[13px] text-ink-2">
          <summary className="cursor-pointer">
            Закрытые записи ({card.history.filter((h) => h.closedOn).length})
          </summary>
          <ul className="mt-2 flex flex-col gap-1">
            {card.history
              .filter((h) => h.closedOn)
              .map((h) => (
                <li key={h.id}>
                  {h.itemName} · {qty(h.quantity)} {h.unit} · выдано {date(h.issuedOn)}, закрыто{' '}
                  {date(h.closedOn ?? '')}
                  {h.note ? ` — ${h.note}` : ''}
                </li>
              ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function DuePanel({ onIssue }: { onIssue: (userId: string) => void }) {
  const due = useQuery(dueQuery);
  const rows = due.data ?? [];
  return (
    <Panel title="Кому выдать или заменить" meta={rows.length ? String(rows.length) : undefined}>
      {due.isPending ? (
        <Spinner />
      ) : rows.length ? (
        <ul className="divide-y divide-line">
          {rows.map((r) => (
            <li
              key={`${r.userId}-${r.itemId}`}
              className="flex flex-col gap-1 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4"
            >
              <div className="min-w-0 flex-1 text-[14px]">
                <span className="font-medium">{r.fullName}</span>
                {r.profession ? <span className="text-ink-2"> · {r.profession}</span> : null}
                <div className="text-[13px] text-ink-2">
                  {r.itemName} · {qty(r.quantity)} {r.unit}
                  {r.replaceOn ? ` · до ${date(r.replaceOn)}` : ''}
                </div>
              </div>
              <Badge tone={STATE_TONE[r.state]}>{PPE_STATES[r.state]}</Badge>
              <Button size="sm" onClick={() => onIssue(r.userId)}>
                К карточке
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">Все СИЗ выданы и в сроке.</p>
      )}
    </Panel>
  );
}

function NormsPanel() {
  const qc = useQueryClient();
  const norms = useQuery(normsQuery);
  const items = useQuery(itemsQuery({ kind: 'material' }));
  const professions = useQuery({
    queryKey: ['wh', 'ppe', 'professions'],
    queryFn: () => api<string[]>('/warehouse/ppe/professions'),
  });
  const [f, setF] = useState({ profession: '', itemId: '', quantity: '1', wearMonths: '12' });
  const save = useMutation({
    mutationFn: () => {
      const body: PpeNormInput = {
        profession: f.profession.trim(),
        itemId: f.itemId,
        quantity: parseNum(f.quantity),
        wearMonths: Number(f.wearMonths),
      };
      return api<PpeNorm>('/warehouse/ppe/norms', { method: 'PUT', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wh', 'ppe'] });
      toast('Норма сохранена');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/warehouse/ppe/norms/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wh', 'ppe'] }),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <Panel title="Нормы выдачи СИЗ по профессиям">
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_100px_100px_auto]">
        <Field label="Профессия (должность)">
          <Input
            list="ppe-professions"
            value={f.profession}
            placeholder="Сварщик"
            onChange={(e) => setF({ ...f, profession: e.target.value })}
          />
          <datalist id="ppe-professions">
            {(professions.data ?? []).map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </Field>
        <Field label="Позиция">
          <Select value={f.itemId} onChange={(e) => setF({ ...f, itemId: e.target.value })}>
            <option value="">Выберите</option>
            {(items.data ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} · {x.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Кол-во">
          <Input
            inputMode="decimal"
            value={f.quantity}
            onChange={(e) => setF({ ...f, quantity: e.target.value })}
          />
        </Field>
        <Field label="Мес. носки">
          <Input
            inputMode="numeric"
            value={f.wearMonths}
            onChange={(e) => setF({ ...f, wearMonths: e.target.value })}
          />
        </Field>
        <div className="flex items-end">
          <Button
            variant="primary"
            className="w-full"
            loading={save.isPending}
            disabled={
              f.profession.trim().length < 2 ||
              !f.itemId ||
              !(parseNum(f.quantity) > 0) ||
              !(Number(f.wearMonths) >= 1)
            }
            onClick={() => save.mutate()}
          >
            Сохранить норму
          </Button>
        </div>
      </div>
      {norms.data?.length ? (
        <ul className="divide-y divide-line text-[14px]">
          {norms.data.map((n) => (
            <li
              key={n.id}
              className="flex flex-col gap-1 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4"
            >
              <span className="min-w-0 flex-1">
                <span className="font-medium">{n.profession}</span> — {n.itemName}, {qty(n.quantity)} {n.unit}{' '}
                на {n.wearMonths} мес.
              </span>
              <Button
                size="sm"
                variant="ghost"
                loading={remove.isPending && remove.variables === n.id}
                onClick={() => remove.mutate(n.id)}
              >
                Убрать
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">Норм пока нет.</p>
      )}
    </Panel>
  );
}

function IssueDialog({ card, onClose }: { card: PpeCard; onClose: () => void }) {
  const qc = useQueryClient();
  const warehouses = useQuery(warehousesQuery);
  const items = useQuery(itemsQuery({ kind: 'material' }));
  const [warehouseId, setWarehouseId] = useState('');
  // По умолчанию — всё, что не выдано или пора заменить
  const [lines, setLines] = useState(
    card.rows
      .filter((r) => r.normId && r.state !== 'ok')
      .map((r, i) => ({ key: `n${i}`, itemId: r.itemId, quantity: String(r.quantity), wearMonths: '' })),
  );
  const [seq, setSeq] = useState(0);
  const m = useMutation({
    mutationFn: () => {
      const body: PpeIssueInput = {
        userId: card.userId,
        warehouseId,
        lines: lines.map((l) => ({
          itemId: l.itemId,
          quantity: parseNum(l.quantity),
          wearMonths: l.wearMonths.trim() ? Number(l.wearMonths) : undefined,
        })),
      };
      return api<{ documentNumber: string }>('/warehouse/ppe/issue', { body });
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast(`Выдано по ${r.documentNumber} — получатель подтвердит в своём приложении`);
      onClose();
    },
  });
  const ok =
    Boolean(warehouseId) && lines.length > 0 && lines.every((l) => l.itemId && parseNum(l.quantity) > 0);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Выдать СИЗ: ${card.fullName}`}
      description="Документ выдачи со склада; получатель подтверждает получение в своём приложении, срок замены считается от даты выдачи"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!ok} onClick={() => m.mutate()}>
            Выдать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Склад">
          <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">Выберите склад</option>
            {(warehouses.data ?? [])
              .filter(
                (w) =>
                  w.canOperate && w.kind !== 'transit' && w.kind !== 'contractor' && w.kind !== 'customer',
              )
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
          </Select>
        </Field>
        {lines.map((l, i) => (
          <div key={l.key} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_90px_90px_auto]">
            <Field label={i === 0 ? 'Позиция' : ''}>
              <Select
                value={l.itemId}
                onChange={(e) =>
                  setLines(lines.map((x, j) => (j === i ? { ...x, itemId: e.target.value } : x)))
                }
              >
                <option value="">Выберите</option>
                {(items.data ?? []).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={i === 0 ? 'Кол-во' : ''}>
              <Input
                inputMode="decimal"
                value={l.quantity}
                onChange={(e) =>
                  setLines(lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))
                }
              />
            </Field>
            <Field label={i === 0 ? 'Мес.' : ''} hint={i === 0 ? 'пусто — по норме' : undefined}>
              <Input
                inputMode="numeric"
                value={l.wearMonths}
                onChange={(e) =>
                  setLines(lines.map((x, j) => (j === i ? { ...x, wearMonths: e.target.value } : x)))
                }
              />
            </Field>
            <Button
              variant="ghost"
              className="h-11"
              onClick={() => setLines(lines.filter((_, j) => j !== i))}
            >
              ×
            </Button>
          </div>
        ))}
        <Button
          variant="secondary"
          onClick={() => {
            setSeq(seq + 1);
            setLines([...lines, { key: `a${seq}`, itemId: '', quantity: '1', wearMonths: '' }]);
          }}
        >
          Добавить позицию
        </Button>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}
